import {
    fetch_application_transition_history,
    fetch_configured_case_gate_ids,
    fetch_queue_onboarding_cases,
    hydrate_case_detail
} from "@/services/onboarding_journey_service";
import { LIST_SENT_CONTRACTS_URL, get_request_config } from "@/utils/system_constants/constant";
import axios from "axios";
import { fetch_template_bindings } from "./esigning_setup_service_helpers";
import { build_sent_contract_params, build_signing_tracker_query, has_failed_response } from "./esigning_setup_computed_helpers";
import { build_summary_state } from "./esigning_setup_request_summary_utils";
import {
    build_preserved_route_query,
    clear_signing_context_data,
    get_selected_case_route,
    get_sent_contracts_error_message,
    handle_sent_contracts_response,
    has_cached_gate_cases,
    hide_system_loader_after_render,
    matches_application_id,
    matches_entity_profile_id,
    matches_esigning_gate,
    reset_signing_request_state,
    reset_case_summary_data,
    show_system_loader
} from "./esigning_setup_methods_utils";

export const esigning_setup_case_methods = {
    initialize_esigning_view(route = this.$route) {
        return this.load_gate_cases()
            .then(() => this.sync_selected_case_from_route(route))
            .then(() => this.load_historical_gate_contexts(this.selected_case))
            .then(() => this.refresh_signing_context_data())
            .then(() => this.sync_active_detail_tab())
            .finally(() => {
                this.sync_signing_request_view_with_route(route);
                this.$nextTick(() => {
                    this.scroll_active_detail_tab_into_view();
                    this.update_detail_tab_overflow();
                });
            });
    },
    load_gate_cases(options = {}) {
        if (!options?.force_reload && has_cached_gate_cases(this)) {
            return Promise.resolve(this.gate_cases);
        }

        this.loading_cases = true;
        this.cases_error = "";
        show_system_loader("Loading e-signing applications...");
        return fetch_queue_onboarding_cases()
            .then((cases) => {
                const resolved_cases = Array.isArray(cases) ? cases : [];
                this.all_cases = resolved_cases;
                this.gate_cases = resolved_cases.filter(matches_esigning_gate);
                this.initialized_gate_cases = true;
            })
            .catch((error) => {
                this.all_cases = [];
                this.gate_cases = [];
                this.initialized_gate_cases = false;
                this.cases_error = error?.response?.data?.message || error?.message || "Failed to load e-signing applications.";
            })
            .finally(() => {
                this.loading_cases = false;
                hide_system_loader_after_render(this);
            });
    },
    sync_selected_case_from_route(route = this.$route) {
        const application_id = Number(route?.params?.applicationId || route?.query?.application_id || route?.query?.applicationId || 0);
        const entity_profile_id = Number(route?.query?.entity_profile_id || route?.query?.entityProfileId || 0);
        const matched_case = (Array.isArray(this.all_cases) ? this.all_cases : []).find((case_item) => matches_application_id(case_item, application_id))
            || (Array.isArray(this.all_cases) ? this.all_cases : []).find((case_item) => matches_entity_profile_id(case_item, entity_profile_id))
            || null;

        if (!matched_case) {
            this.selected_case = null;
            this.configured_gate_ids = [];
            this.workflow_transition_history = [];
            this.loading_selected_case = false;
            this.opening_case_id = null;
            reset_case_summary_data(this);
            this.close_new_signing_request();
            return Promise.resolve();
        }

        this.loading_selected_case = true;
        this.selected_case = application_id ? { ...matched_case, application_id: Number(matched_case?.application_id || matched_case?.id || application_id) } : matched_case;
        show_system_loader("Loading e-signing application...");
        return hydrate_case_detail(matched_case)
            .then((hydrated_case) => {
                this.selected_case = hydrated_case || matched_case;
                return Promise.all([
                    this.load_configured_gate_ids_for_selected_case(this.selected_case),
                    this.load_transition_history_for_selected_case(this.selected_case)
                ]);
            })
            .catch(() => {
                this.selected_case = matched_case;
                return Promise.all([
                    this.load_configured_gate_ids_for_selected_case(this.selected_case),
                    this.load_transition_history_for_selected_case(this.selected_case)
                ]);
            })
            .finally(() => {
                this.loading_selected_case = false;
                this.opening_case_id = null;
                hide_system_loader_after_render(this);
            });
    },
    load_configured_gate_ids_for_selected_case(case_item = {}) {
        return fetch_configured_case_gate_ids(case_item)
            .then((configured_gate_ids) => {
                this.configured_gate_ids = configured_gate_ids;
                return this.configured_gate_ids;
            })
            .catch(() => {
                this.configured_gate_ids = [];
                return this.configured_gate_ids;
            });
    },
    load_transition_history_for_selected_case(case_item = {}) {
        const application_id = Number(case_item?.application_id || case_item?.id || 0);
        if (!application_id) {
            this.workflow_transition_history = [];
            return Promise.resolve(this.workflow_transition_history);
        }

        return fetch_application_transition_history(application_id)
            .then((history_rows) => {
                this.workflow_transition_history = Array.isArray(history_rows) ? history_rows : [];
                return this.workflow_transition_history;
            })
            .catch(() => {
                this.workflow_transition_history = [];
                return this.workflow_transition_history;
            });
    },
    refresh_signing_context_data() {
        if (!this.has_signing_context) {
            clear_signing_context_data(this);
            return Promise.resolve();
        }
        return Promise.all([this.load_template_bindings(), this.load_sent_contracts()]);
    },
    sync_active_detail_tab() {
        const available_tab_ids = this.detail_tabs_with_history.map((tab) => tab.id);
        if (this.active_detail_tab !== "case-history" && !available_tab_ids.includes(this.active_detail_tab)) {
            this.active_detail_tab = this.historical_gate_tabs[0]?.id || "signing";
        }
        this.$nextTick(() => {
            this.scroll_active_detail_tab_into_view();
            this.update_detail_tab_overflow();
        });
    },
    set_active_detail_tab(tab_id) {
        this.active_detail_tab = tab_id;
        this.$nextTick(() => {
            this.scroll_active_detail_tab_into_view();
            this.update_detail_tab_overflow();
        });
    },
    set_active_detail_shell_tab(tab_id = "action") {
        if (tab_id === "reference") {
            this.set_active_detail_tab(this.detail_reference_tab?.id || "application");
            return;
        }

        if (tab_id === "history") {
            this.set_active_detail_tab(this.detail_history_tabs[0]?.id || "case-history");
            return;
        }

        this.set_active_detail_tab(this.detail_tabs_with_history[0]?.id || "signing");
    },
    set_summary_detail_tab(tab_id) { this.active_summary_detail_tab = tab_id; },
    toggle_summary_detail_fullscreen() { this.is_summary_detail_fullscreen = !this.is_summary_detail_fullscreen; },
    scroll_detail_tabs(direction = "right") {
        const tab_scroller = this.$refs.detailTabsScroller;
        if (!tab_scroller) return;

        const scroll_offset = Math.max(180, Math.round(tab_scroller.clientWidth * 0.6));
        const left = direction === "left"
            ? tab_scroller.scrollLeft - scroll_offset
            : tab_scroller.scrollLeft + scroll_offset;
        tab_scroller.scrollTo({ left, behavior: "smooth" });
    },
    update_detail_tab_overflow() {
        const tab_scroller = this.$refs.detailTabsScroller;
        if (!tab_scroller) {
            this.detail_tab_overflow = { left: false, right: false };
            return;
        }

        const max_scroll_left = Math.max(0, tab_scroller.scrollWidth - tab_scroller.clientWidth);
        this.detail_tab_overflow = {
            left: tab_scroller.scrollLeft > 8,
            right: tab_scroller.scrollLeft < max_scroll_left - 8
        };
    },
    scroll_active_detail_tab_into_view() {
        const tab_scroller = this.$refs.detailTabsScroller;
        const active_tab = tab_scroller?.querySelector?.(`[data-detail-tab-id="${this.active_detail_tab}"]`);
        if (!tab_scroller || !active_tab) return;

        active_tab.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "center"
        });
    },
    selected_case_summary_subtitle() {
        if (!this.selected_case_summary) return "";
        return [this.selected_case_summary?.detail, this.selected_case_summary?.activation, this.selected_case_summary?.eta].filter(Boolean).join(" · ") || this.selected_case_summary?.stage || "";
    },
    async open_case_summary(case_item = {}) {
        if (!case_item) return;
        show_system_loader("Loading e-signing drawer...");
        this.selected_case_summary = case_item;
        this.active_summary_detail_tab = "summary";
        this.is_summary_detail_fullscreen = false;
        Object.assign(this, build_summary_state(case_item, {
            transition_history: this.workflow_transition_history,
            configured_gate_ids: this.configured_gate_ids
        }), { is_loading_summary_detail: true });
        try {
            const [hydrated_case, configured_gate_ids, transition_history] = await Promise.all([
                hydrate_case_detail(case_item),
                fetch_configured_case_gate_ids(case_item).catch(() => []),
                fetch_application_transition_history(Number(case_item?.application_id || case_item?.id || 0)).catch(() => [])
            ]);
            this.selected_case_summary = hydrated_case || case_item;
            Object.assign(this, build_summary_state(hydrated_case || case_item, {
                configured_gate_ids,
                transition_history
            }));
        } catch {
            this.selected_case_summary = case_item;
        } finally {
            this.is_loading_summary_detail = false;
            hide_system_loader_after_render(this);
        }
    },
    close_case_summary() { reset_case_summary_data(this); },
    open_selected_summary_case_detail() {
        if (!this.selected_case_summary) return;
        this.open_case_detail(this.selected_case_summary);
        this.close_case_summary();
    },
    open_case_detail(case_item = {}, event) {
        event?.stopPropagation?.();
        const application_id = Number(case_item?.application_id || case_item?.id || 0);
        if (!application_id || this.opening_case_id === application_id) return;
        this.opening_case_id = application_id;
        show_system_loader("Opening e-signing application...");
        this.$router.push(get_selected_case_route(this, application_id));
    },
    go_back_to_esigning_queue() {
        show_system_loader("Loading e-signing applications...");
        this.$router.push({ name: "OnboardingEsigningSetup", query: build_preserved_route_query(this.$route) });
    },
    async load_template_bindings() {
        this.template_bindings_loading = true;
        this.template_bindings_error = "";
        show_system_loader("Loading signing templates...");
        try {
            const result = await fetch_template_bindings(this);
            this.entity_config_id = result.entity_config_id;
            this.template_bindings = result.template_bindings;
            this.selected_template = result.selected_template;
            this.template_bindings_error = result.template_bindings_error;
        } catch (error) {
            this.template_bindings = [];
            this.template_bindings_error = error?.message || "Failed to load template bindings.";
            this.show_error(this.template_bindings_error);
        } finally {
            this.template_bindings_loading = false;
            hide_system_loader_after_render(this);
        }
    },
    async load_sent_contracts() {
        this.sent_contracts_loading = true;
        this.sent_contracts_error = "";
        show_system_loader("Loading sent contracts...");
        try {
            const response = await axios.get(LIST_SENT_CONTRACTS_URL, get_request_config({ params: build_sent_contract_params(this) }));
            handle_sent_contracts_response(this, this.parse_response_data(response.data), has_failed_response);
        } catch (error) {
            this.sent_contracts = [];
            this.sent_contracts_error = get_sent_contracts_error_message(this, error);
        } finally {
            this.sent_contracts_loading = false;
            hide_system_loader_after_render(this);
        }
    },
    open_new_signing_request() {
        if (!this.can_manage_signing_requests) {
            this.show_info("You can track your signing documents here, but only staff can create or send new signing requests.");
            return;
        }
        if (!this.has_signing_context) {
            this.show_info("Start a signing request from a specific onboarding in My Queue so the entity profile and signer context are available.");
            return;
        }
        this.show_new_signing_request = true;
        reset_signing_request_state(this);
    },
    open_signing_tracker(document) {
        const tracker_query = { ...build_signing_tracker_query(document) };
        if (!tracker_query.entity_profile_id && this.route_entity_profile_id) tracker_query.entity_profile_id = String(this.route_entity_profile_id);
        if (!tracker_query.entity_type_id && this.route_entity_type_id) tracker_query.entity_type_id = String(this.route_entity_type_id);
        if (!tracker_query.application_id && this.route_application_id) tracker_query.application_id = String(this.route_application_id);
        this.$router.push({ name: "OnboardingSigningTracker", query: tracker_query });
    },
    set_signing_filter(filter_id) { this.active_signing_filter = filter_id; }
};
